import React, { useState } from "react";
import { Alert, ScrollView, StyleSheet, Text, View } from "react-native";
import * as Sentry from "@sentry/react-native";

import type { AppUserSession } from "@/schemas/auth/session";
import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";
import IdentityService from "@/services/user/identity";
import type { SetOnboardingStageRequest } from "@/schemas/user/identity";
import { getNextOnboardingStage } from "@/utils/onboarding";
import { useSession } from "@/context/auth/useSession";
import ActionButton from "@/Components/Buttons/ActionButton";
import type { AttributeValue } from "@/schemas/attribute/AttributeSet";
import type { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import ProfileService from "@/services/user/profile";

type Props = {
  session: AppUserSession;
  stageMainComponent: React.JSX.Element;
  canContinue: boolean;
  title: string;
  buttonLabel?: string; // we could get rid of this and add a isLastOnboardingStage utility function to determine if user is at the last stage
  subtitle?: string;
  aboveTitleComponent?: React.JSX.Element;
  attributeType?: UserAttributeType;
  attributeSelections?: AttributeValue[];
};

function OnboardingBaseScreen({
  session,
  stageMainComponent,
  canContinue,
  title,
  buttonLabel = "Continue",
  subtitle,
  aboveTitleComponent,
  attributeType,
  attributeSelections,
}: Props): React.JSX.Element {
  const { setIdentity } = useSession();

  const [isLoading, setIsLoading] = useState(false);

  const onboardingStage = session.identity.onboardingStage;
  const nextStage = getNextOnboardingStage(onboardingStage);

  const handleNextButtonPress = async (): Promise<void> => {
    const identityService = new IdentityService(session);
    const profileService = new ProfileService(session);

    const requestBody: SetOnboardingStageRequest = {
      stage: nextStage,
    };
    const updatedLocalIdentity = {
      ...session.identity,
      onboardingStage: nextStage,
    };

    try {
      if (isLoading) return;
      setIsLoading(true);

      // Save the user's selection to the backend
      if (attributeType && attributeSelections) {
        await profileService.updateAttributes(
          attributeType,
          attributeSelections.map((item) => item.id),
        );
      }

      // Save the user's onboarding stage to the backend
      await identityService.setOnboardingStage(requestBody);

      // Update onboardingStage in UserIdentity locally
      setIdentity(updatedLocalIdentity);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert(
        "Oops",
        "Something went wrong. Please try again or reach out to support@getkaiapp.com",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollViewContent}>
        <View style={styles.mainContentContainer}>
          {aboveTitleComponent && (
            <View style={styles.aboveTitleContainer}>
              {aboveTitleComponent}
            </View>
          )}
          <View style={styles.titleContainer}>
            <Text style={styles.title}>{title}</Text>
            {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
          </View>
          {stageMainComponent}
        </View>
        <View style={styles.buttonContainer}>
          <ActionButton
            label={buttonLabel}
            variant="filled"
            isDisabled={!canContinue}
            onPress={() => handleNextButtonPress()}
            isLoading={isLoading}
          />
        </View>
      </ScrollView>
    </View>
  );
}

const currentTheme = themes.morning;
const styles = StyleSheet.create({
  container: {
    justifyContent: "space-between",
    flex: 1,
    padding: Spacings.s1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  mainContentContainer: {
    flex: 1,
  },
  aboveTitleContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  titleContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: Spacings.s3,
    paddingHorizontal: Spacings.s3,
  },
  title: {
    fontSize: currentTheme.typography.h1.size.mobile * 1.35,
    fontWeight: "bold",
    marginBottom: 5,
    color: currentTheme.text,
  },
  subtitle: {
    fontSize: currentTheme.typography.h2.size.mobile,
    color: currentTheme.subtext,
    textAlign: "center",
    marginBottom: Spacings.s3,
  },
  buttonContainer: {
    marginTop: Spacings.s6,
    marginBottom: Spacings.s2,
    width: "90%",
    alignSelf: "center",
  },
});

export default OnboardingBaseScreen;
